import type { EnumToLiteral } from 'shared/types/ts-helpers';
import isFunction from 'lodash/isFunction';

const FONT_FAMILY = 'VTBGroupUIWebBook, sans-serif';

enum Unit {
  Pixel = 'px',
  RootElement = 'rem',
  Emphasized = 'em',
  Percent = '%'
}

const getSizeUnit = (size: number | string, unit: (typeof Unit)[keyof typeof Unit] = Unit.RootElement) => {
  if (typeof size === 'string') {
    return size;
  }

  switch (unit) {
    case 'px':
      return `${size}px`;
    case '%':
      return `${size}%`;
    case 'em':
      return `${+size / 10}em`;
    case 'rem':
    default:
      return `${+size / 10}rem`;
  }
};

export const getCssVar = (prefix: string, key: string | number): string => `var(--dion-${prefix}-${key})`;

export enum Breakpoints {
  XXS = 'xxs',
  Mobile = 'mobile',
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  Tablet = 'tablet',
  Desktop = 'desktop',
  LG = 'lg',
  XL = 'xl',
  XXL = 'xxl'
}

export type Breakpoint = EnumToLiteral<Breakpoints>;

const hexToRgb = (hex: string, alpha = 1, isRgbOnly = false) => {
  const channels = parseInt(hex.slice(1, 7), 16);

  const r = (channels >> 16) & 255;
  const g = (channels >> 8) & 255;
  const b = channels & 255;

  const type = isRgbOnly ? 'rgb' : 'rgba';
  const rgb = [r, g, b];

  if (!isRgbOnly) {
    rgb.push(alpha);
  }

  return `${type}(${rgb.join(',')})`;
};

const red = {
  25: '#FFF7F7',
  50: '#FEE6E6',
  100: '#FDCECE',
  200: '#FB9D9D',
  300: '#FA7979',
  400: '#F94C4C',
  500: '#F60909',
  600: '#DB0808',
  700: '#AC0606',
  800: '#940505',
  900: '#7B0404',
  950: '#620404',
  975: '#4A0303'
};

const grey = {
  25: '#FAFCFF',
  50: '#F5F7F9',
  100: '#EBEDF0',
  200: '#D6DBE4',
  300: '#C4CBD6',
  400: '#A9B0BC',
  500: '#898E96',
  600: '#666A73',
  700: '#4E5158',
  800: '#43464B',
  900: '#3A3C41',
  950: '#292A2E',
  975: '#202124'
};

const green = {
  25: '#F7FDF9',
  50: '#EAFAF0',
  100: '#D6F5E1',
  200: '#ADEBC4',
  300: '#84E1A6',
  400: '#46D27A',
  500: '#2FC063',
  600: '#28A455',
  700: '#23904B',
  800: '#1E7B40',
  900: '#196735',
  950: '#14522B',
  975: '#0F3E20'
};

const black = {
  20: hexToRgb('#111314', 0.2),
  40: hexToRgb('#111314', 0.4),
  60: hexToRgb('#111314', 0.6),
  80: hexToRgb('#111314', 0.8),
  100: '#111314'
};

const blue = {
  25: '#F5F9FF',
  50: '#E5EFFF',
  100: '#CCDFFF',
  200: '#99C0FF',
  300: '#68A2FF',
  400: '#3381FF',
  500: '#0062FF',
  600: '#004ECC',
  700: '#0044B2',
  800: '#003B99',
  900: '#003180',
  950: '#002766',
  975: '#001D4C',
  // camelCase will be transformed to cebab-case
  'post-quant': '#689FFF'
};

const BREAKPOINTS = {
  [Breakpoints.XXS]: 270,
  [Breakpoints.Mobile]: 370,
  [Breakpoints.XS]: 270,
  [Breakpoints.SM]: 576,
  [Breakpoints.MD]: 768,
  [Breakpoints.Tablet]: 768,
  [Breakpoints.Desktop]: 1024,
  [Breakpoints.LG]: 1024,
  [Breakpoints.XL]: 1440,
  [Breakpoints.XXL]: 2560
};

const PALETTE = {
  primary: blue[500],
  secondary: grey[100],
  info: grey[900],
  success: green[500],
  error: red[300],
  text: {
    primary: grey[100],
    secondary: grey[500]
  }
};

declare module '@dion-web/ui/mui/material/styles' {
  interface TypographyVariants {
    caption2?: React.CSSProperties;
    subtitle3: React.CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    caption2?: React.CSSProperties;
    subtitle3?: React.CSSProperties;
  }

  interface BreakpointOverrides {
    xxs: true;
    xs: true;
    sm: true;
    md: true;
    lg: true;
    xl: true;
    xxl: true;
    mobile: true;
    tablet: true;
    desktop: true;
  }
}

// Update the Typography's variant prop options
declare module '@dion-web/ui/mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    caption2: true;
    subtitle3: true;
  }
}

// TODO утащить всё что можно в #uikit

export const DARK_THEME = {
  breakpoints: {
    step: 4,
    keys: Object.keys(BREAKPOINTS),
    values: BREAKPOINTS
  },
  palette: {
    mode: 'dark',
    primary: { main: PALETTE.primary },
    secondary: {
      // Buttons
      main: PALETTE.secondary
    },
    info: {
      // Buttons
      main: PALETTE.info
    },
    success: { main: PALETTE.success },
    error: {
      // Buttons
      main: PALETTE.error
    },
    text: PALETTE.text,
    background: { default: black[100] },
    tonalOffset: 0.115
  },
  components: {
    MuiListItem: { styleOverrides: { root: { width: 'auto' } } },
    MuiAccordion: {},
    MuiButton: {
      styleOverrides: {
        sizeLarge: {
          height: getSizeUnit(48),
          padding: '0 1.6rem',
          borderRadius: '.6rem',
          fontSize: '14px',
          lineHeight: '18px',
          fontWeight: 500,
          letterSpacing: '0.1px'
        },
        sizeMedium: {
          height: getSizeUnit(38),
          padding: '0 1.6rem',
          fontSize: '14px',
          lineHeight: '18px',
          fontWeight: 500
        },
        sizeSmall: {
          height: getSizeUnit(32),
          padding: '0 1.2rem',
          fontSize: '14px',
          lineHeight: '18px',
          fontWeight: 500
        },
        containedPrimary: {
          background: getCssVar('blue', 500),
          color: getCssVar('grey', 100),
          '&:not(.Mui-disabled):hover': { background: getCssVar('blue', 700) },
          '&:not(.Mui-disabled):active': { background: getCssVar('blue', 900) }
        },
        containedSecondary: {
          background: getCssVar('grey', 900),
          color: getCssVar('grey', 100),
          '&:not(.Mui-disabled):hover': { background: getCssVar('grey', 950) },
          '&:not(.Mui-disabled):active': { background: getCssVar('grey', 975) }
        },
        textPrimary: {
          color: getCssVar('blue', 300),
          '&:hover': {
            background: 'transparent',
            color: getCssVar('blue', 400)
          },
          '&:active': {
            background: 'transparent',
            color: getCssVar('blue', 500)
          }
        },
        textSecondary: {
          color: getCssVar('grey', 100),
          '&:hover': { background: getCssVar('grey', 950) },
          '&:active': { background: getCssVar('grey', 975) }
        },
        textError: {
          background: getCssVar('grey', 900),
          color: getCssVar('red', 300),
          '&:hover': {
            background: getCssVar('grey', 900),
            color: getCssVar('red', 400)
          },
          '&:active': {
            background: getCssVar('grey', 975),
            color: getCssVar('red', 400)
          }
        }
      }
    },
    MuiLink: {
      styleOverrides: {
        root: ({ color = 'primary' }) => {
          // Control hover state
          const linkState =
            (color === 'primary' && {
              color: getCssVar('blue', 300),
              '&:hover': { color: getCssVar('blue', 500) },
              '&:active': { color: getCssVar('blue', 700) }
            }) ||
            {};

          return {
            fontSize: getCssVar('font', 'common'),
            fontWeight: getCssVar('font', 'semi-bold'),
            textDecoration: 'none',
            transition: 'all .27s',
            cursor: 'pointer',
            ...linkState
          };
        }
      }
    },
    MuiSlider: {
      styleOverrides: {
        thumbSizeSmall: {
          width: 'auto',
          height: 'inherit'
        }
      }
    },
    MuiModal: {
      styleOverrides: {
        root: () => ({
          outline: 'none',
          border: 'none',
          '&.MuiModal-hidden': { zIndex: -1 }
        })
      }
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: (args) => {
          const hasEndIconHandler = isFunction(args?.ownerState?.endAdornment?.props?.children?.props?.onClick);

          return {
            '& input.MuiInputBase-input': {
              padding: 0
            },
            '& .MuiInputAdornment-positionEnd svg': {
              cursor: hasEndIconHandler ? 'pointer !important' : 'inherit'
            }
          };
        }
      }
    },
    MuiTextField: {
      styleOverrides: {
        root: () => ({
          '& .MuiInputBase-root, &.MuiInputBase-root': {
            border: 'none',
            outline: 'none',
            padding: '8px 12px',
            borderRadius: '4px',
            width: '100%',
            minHeight: '38px',
            backgroundColor: 'var(--mui-palette-elevation0-transparent)', //grey[900],
            boxShadow: 'none',
            '&.Mui-focused': { backgroundColor: grey[800] }, // TODO
            transition: 'background-color 0.2s ease',
            '&::after, &::before': { display: 'none' },
            '&.Mui-error': {
              outline: '1px solid var(--mui-palette-elevation0-errorContent)' //`1px solid ${red['300']}`,
            }
          },
          '& input.MuiInputBase-input': {
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          },
          '& .MuiInputBase-input': {
            padding: 0,
            '&::placeholder': {
              fontSize: 'inherit',
              color: 'var(--mui-palette-elevation0-lowContrast)', //grey[500],
              fontFamily: 'inherit'
            }
          },
          '& .MuiFormHelperText-root': {
            mx: 0,
            mt: 1,
            '&.Mui-error *': { color: 'var(--mui-palette-elevation0-errorContent)' } // red['300'] },
          },
          fontFamily: FONT_FAMILY,
          fontWeight: getCssVar('font', 'semi-bold')
        })
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: getCssVar('font', 'common')
        }
      }
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: 'var(--mui-palette-brand-main)', //green[500], // Новый цвет галочки
            '& input': {
              backgroundColor: 'var(--mui-palette-brand-contrast)' //'#ff0000', // Новый фон заливки
            }
          }
        }
        // checked: {
        //   '& input': {
        //     backgroundColor: 'var(--mui-palette-brand-contrast)', //'#ff0000', // Новый фон заливки
        //   },
        // },
      }
    },
    MuiMenu: {
      styleOverrides: {
        root: {
          '& > .MuiMenu-paper': {
            backgroundColor: 'var(--mui-palette-elevation2-main) !important',
            backgroundImage: 'unset',
            minWidth: '120px'
          }
        }
      }
    }
  }
};
